/**
 * Ownership of the portal-issued access token (MA-227): whether to ask for one,
 * when to ask again, and when to stop asking altogether.
 *
 * `portalToken.ts` performs one exchange and reports what happened.
 * Everything policy-shaped lives here, because three different callers need the
 * same answers — `acquireAccessToken` on every API call, `fetchWithAuth` on a
 * 401, and `EmbedAuthGate` once on mount — and none of them should be deciding
 * for itself.
 *
 * REFRESH IS OURS. Not MSAL's (it never sees this token; `useIsAuthenticated()`
 * stays false throughout) and not the gate's (it runs once and then renders
 * children for the rest of the page's life). The portal returns no expiry, so we
 * decode `exp` from the token and re-exchange while a margin of life remains,
 * which is what Synigo's own token service does on their side.
 *
 * The token lives in a module-scope variable and NOWHERE else — no
 * sessionStorage, no localStorage. Same reasoning as the `cacheLocation` note in
 * `lib/teams/auth.ts`: this audience includes shared machines, browser storage
 * would leave a valid bearer token for whoever sits down next, and it buys
 * nothing here because the portal can always mint another one.
 *
 * The only place this module hands the token to is `lib/api/utils.ts`, which puts
 * it in an `Authorization: Bearer` header on a request to our own backend.
 * Nothing is ever posted back to the portal, and it is never written to storage.
 *
 * That is the guarantee, and it is narrower than "the token never leaves the
 * page". Once the header is set, the request passes through the Application
 * Insights fetch/XHR instrumentation configured in `lib/telemetry.ts`, which sets
 * `enableRequestHeaderTracking: true`. Headers are redacted there today only
 * because `@microsoft/applicationinsights-dependencies-js` defaults its
 * `ignoreHeaders` list to include `Authorization` -- a package default, behind a
 * caret range in `package.json`, not something we assert. Do not upgrade this
 * comment back to "the token never leaves this module" without an explicit
 * `ignoreHeaders` in the telemetry config to back it up.
 */

import { getEmbedConfig } from "@/lib/embed/config";
import { requestPortalToken } from "@/lib/embed/portalToken";

/**
 * Re-exchange once the token has less than this much life left.
 *
 * Two minutes covers the clock skew between the browser and the backend plus the
 * flight time of a request that is about to be sent, which is the whole job: a
 * token that passes this check must still be valid when the API validates it.
 */
export const EXP_SKEW_MS = 120_000;

/**
 * How long to hold a token whose `exp` we could not read.
 *
 * The alternative is treating "no expiry" as "expired", which would mean a fresh
 * postMessage round trip before every single API call — a request per keystroke
 * on the config fetch, and a dependency on the portal answering promptly for the
 * app to work at all. Five minutes is short enough that a genuinely dead token
 * resolves into the 401 path quickly and long enough that a burst of calls costs
 * one exchange.
 */
export const FALLBACK_TTL_MS = 300_000;

interface CachedToken {
  token: string;
  expiresAtMs: number;
}

let cached: CachedToken | undefined;

/**
 * Page-load latch: once our own backend has refused a portal token, stop
 * offering portal tokens for the rest of this page load.
 *
 * This is what makes the popup fallback reachable. Dropping the cached token on
 * a 401 is not enough on its own — the very next call would ask the portal, get
 * the same token back (the portal's session has not changed), send it, and be
 * refused again, forever, while the gate sits on a chat in which nothing works.
 * The latch converts "this token is rejected" into "this PATH is rejected", and
 * with the path closed `acquireAccessToken` falls through to MSAL and the gate
 * shows the normal "Aanmelden met Microsoft" button.
 *
 * Per page load, not persisted: a reload is a free retry, and the cause may well
 * have been fixed in between (a propagating Entra pre-authorization, most
 * likely).
 */
let latched = false;

/**
 * How long to leave the portal alone after an exchange fails.
 */
export const FAILURE_COOLDOWN_MS = 60_000;

/**
 * When the portal may be asked again after a failure. 0 means "now".
 *
 * `acquireAccessToken` calls into here on EVERY API call, and a failed exchange
 * leaves nothing cached, so without this a portal that does not answer costs
 * every request in the page -- every chat turn, long after the user signed in via
 * the popup -- a fresh postMessage and the full `PORTAL_TOKEN_TIMEOUT_MS` wait
 * (2500 ms, `portalToken.ts`) before falling through to MSAL. That is 2.5 s added
 * to each turn for a path that has already proven it has nothing to give.
 *
 * NOT the same thing as `latched` above, and deliberately not merged with it:
 *
 *   - `latched` means "OUR BACKEND refused a portal token". That is a
 *     configuration fact -- the pre-authorization is missing, or the audience is
 *     wrong -- and it will not fix itself within a page load, so it is permanent
 *     until reload.
 *   - this cool-down means "the portal did not answer THIS TIME". A portal that
 *     is briefly unreachable, mid-deploy, or slow must recover on its own without
 *     the user reloading, so it expires.
 *
 * Making either one behave like the other is a regression: a permanent latch here
 * would turn one slow moment into a popup for the rest of the session, and a
 * cool-down there would re-send a token we know is refused, once a minute,
 * forever.
 */
let cooldownUntilMs = 0;

/**
 * Whether the portal token path is available at all.
 *
 * A conjunction, and fail-closed on every term: embed build, flag on, a parent
 * origin to post to and validate against, a tenant to mint in, a resource to
 * mint for, and the latch not thrown. Anything missing and this returns false,
 * which everywhere downstream means "behave exactly as before MA-227".
 */
export function isPortalTokenEnabled(): boolean {
  const { embedMode, portalTokenEnabled, allowedParentOrigin, tenantId, portalTokenResource } =
    getEmbedConfig();

  return Boolean(
    embedMode &&
      portalTokenEnabled &&
      allowedParentOrigin &&
      tenantId &&
      portalTokenResource &&
      !latched,
  );
}

/**
 * Read the `exp` claim (seconds since the epoch) out of a JWT, without verifying
 * anything.
 *
 * Unverified on purpose, and safe to be: the value is used only to decide when to
 * ask the portal for another token. A token whose `exp` is a lie is still
 * validated properly by the backend, so the worst a wrong answer buys is an
 * unnecessary exchange or an unnecessary 401.
 *
 * Never throws. A JWT here is a string from another origin's postMessage, so
 * every step of it is untrusted input: the segment count, the base64url padding,
 * whether the payload is JSON at all, and whether `exp` is a number. Returns
 * `undefined` for all of it and lets the caller fall back to a fixed TTL.
 *
 * Exported for tests — nothing else should need it.
 */
export function decodeJwtExp(token: string): number | undefined {
  try {
    const segments = token.split(".");
    if (segments.length !== 3) return undefined;

    // base64url -> base64. `atob` rejects `-`/`_` and requires the padding that
    // JWTs omit.
    const base64 = segments[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );

    const claims: unknown = JSON.parse(atob(padded));
    if (typeof claims !== "object" || claims === null) return undefined;

    const exp = (claims as Record<string, unknown>).exp;

    // `Number.isFinite`, not `typeof === "number"`. `NaN` and `Infinity` are both
    // numbers and both parse out of JSON (`{"exp": 1e400}` is `Infinity`), and
    // each breaks the cache in its own direction: `NaN` makes every comparison
    // against it false, so the token is never considered fresh and every single
    // call performs a new exchange; `Infinity` makes it fresh forever, pinning a
    // dead token for the whole page load. `> 0` rejects the negative and zero
    // cases for the same reason -- an `exp` from another origin's postMessage is
    // untrusted input, so it is either a usable timestamp or it is nothing and we
    // fall back to `FALLBACK_TTL_MS`.
    //
    // The `typeof` term is there only to narrow `unknown` for the compiler --
    // `Number.isFinite` already answers false for every non-number.
    return typeof exp === "number" && Number.isFinite(exp) && exp > 0
      ? exp
      : undefined;
  } catch {
    return undefined;
  }
}

/** When a token should be considered spent, from its own `exp` or the fallback. */
function expiryOf(token: string, nowMs: number): number {
  const exp = decodeJwtExp(token);
  return exp === undefined ? nowMs + FALLBACK_TTL_MS : exp * 1000;
}

/**
 * The current portal access token, acquiring or refreshing one if needed.
 *
 * NEVER throws and never rejects. Every failure — not configured, latched,
 * cooling down from the last failure, portal refused, portal silent, timeout,
 * malformed reply, a token that is already spent — comes back as
 * `undefined`, because every caller's response to all of them is identical: fall
 * through to the flow that existed before this path did. Making them
 * distinguishable would only invite a caller to handle them differently, and
 * there is nothing different to do.
 */
export async function getPortalAccessToken(): Promise<string | undefined> {
  if (!isPortalTokenEnabled()) return undefined;

  const now = Date.now();
  if (cached && cached.expiresAtMs - now > EXP_SKEW_MS) {
    return cached.token;
  }

  // Still cooling down from a failed exchange (see `cooldownUntilMs`). Checked
  // after the cache, not before it: a live cached token is usable no matter what
  // the last attempt did.
  if (now < cooldownUntilMs) return undefined;

  try {
    const token = await requestPortalToken();
    const expiresAtMs = expiryOf(token, Date.now());

    // The freshness test applies to a token we have just been handed, not only to
    // one we are reading back out of the cache. Without this check an already
    // expired token -- a portal whose own cached token went stale, or a clock
    // well out of step with ours -- is sent once, 401s, and burns the single
    // retry in `fetchWithAuth` plus the permanent latch before MSAL is tried.
    // Cheaper to notice here and fall straight through to MSAL.
    if (expiresAtMs - Date.now() <= EXP_SKEW_MS) {
      console.warn(
        "[portal-token] portal returned an already-spent token; falling back",
      );
      cached = undefined;
      // No cool-down on this branch, on purpose. A portal answering with a stale
      // token IS answering, and the next answer may well be a live one, so we do
      // not want to be blind to it for a minute. The cool-down is for a portal
      // that refuses or says nothing at all.
      return undefined;
    }

    cached = { token, expiresAtMs };
    return token;
  } catch (e) {
    // Deliberately not an `console.error`: with the flag on and a portal that
    // does not implement the exchange, this is the expected outcome on every
    // page load and the user still gets a working app via the popup.
    console.warn("[portal-token] no token from the portal; falling back", e);
    cached = undefined;
    cooldownUntilMs = Date.now() + FAILURE_COOLDOWN_MS;
    return undefined;
  }
}

/**
 * Give up on the portal token path for the rest of this page load, IF the token
 * that was just refused came from the portal.
 *
 * Returns whether a portal token was actually cached, which is the caller's
 * signal that a retry is worth making: `fetchWithAuth` re-sends its request once
 * only when this says yes, so a 401 on an MSAL or Teams token cannot trigger a
 * pointless second attempt.
 *
 * The latch is set on that same condition, and only on it. It used to be set
 * unconditionally, which meant a single 401 on an MSAL token -- an expired
 * session, a backend hiccup, anything -- permanently disabled the portal path for
 * the rest of the page load even though the portal had never been asked for
 * anything and had refused nothing. The latch is for one fact only: "our backend
 * refused a token THE PORTAL gave us".
 */
export function invalidatePortalToken(): boolean {
  const had = cached !== undefined;
  cached = undefined;
  if (had) latched = true;
  return had;
}
